import React, { useState, useEffect, useRef } from 'react';
import {
  CHANNEL_LABELS,
  CHANNELS,
  Channel,
  DEFAULT_CHANNELS,
  InstallerUploadResult,
  PLATFORMS,
  Platform,
  ReaderVersion,
  installerExtensions
} from '../types';
import InstallerUploadField from './InstallerUploadField';
import { buildInstallerPayload } from '../utils/buildInstallerPayload';

const VERSION_PATTERN = /^\d+\.\d+(\.\d+)?$/;

interface CreateReaderVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<ReaderVersion>) => Promise<void>;
  editingVersion?: ReaderVersion | null;
  isLoading?: boolean;
}

const CreateReaderVersionModal: React.FC<CreateReaderVersionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingVersion,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    platform: '',
    channel: '',
    version: '',
    mandatory: false,
    active: true,
    release_notes: '',
    download_url: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const [invalidSubmitCount, setInvalidSubmitCount] = useState(0);
  const [uploadedInstaller, setUploadedInstaller] = useState<InstallerUploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Reset form when modal opens/closes or editing version changes
  useEffect(() => {
    if (isOpen) {
      if (editingVersion) {
        setFormData({
          platform: editingVersion.platform,
          channel: editingVersion.channel || DEFAULT_CHANNELS[editingVersion.platform],
          version: editingVersion.version,
          mandatory: editingVersion.mandatory,
          active: editingVersion.active,
          release_notes: editingVersion.release_notes || '',
          download_url: editingVersion.download_url || ''
        });
      } else {
        setFormData({
          platform: '',
          channel: '',
          version: '',
          mandatory: false,
          active: true,
          release_notes: '',
          download_url: ''
        });
      }
      setErrors({});
      setInvalidSubmitCount(0);
      setUploadedInstaller(null);
      setIsUploading(false);
    }
  }, [isOpen, editingVersion]);

  // Nút gửi nằm ở chân modal nên luôn thấy, còn ô lỗi có thể đang nằm ngoài
  // vùng cuộn của thân form — không kéo tới thì bấm xong trông như không có gì xảy ra
  useEffect(() => {
    if (invalidSubmitCount === 0) {
      return;
    }
    const target = formRef.current?.querySelector<HTMLElement>('[data-invalid]');
    target?.scrollIntoView({ block: 'center' });
    target?.focus({ preventScroll: true });
  }, [invalidSubmitCount]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.platform) {
      newErrors.platform = 'Vui lòng chọn nền tảng';
    }

    if (formData.platform && !formData.channel) {
      newErrors.channel = 'Vui lòng chọn kênh phát hành';
    }

    if (!formData.version) {
      newErrors.version = supportsInstaller
        ? 'Upload file cài đặt để lấy phiên bản từ file mô tả'
        : 'Vui lòng nhập phiên bản';
    } else if (!VERSION_PATTERN.test(formData.version)) {
      newErrors.version = 'Phiên bản phải theo định dạng x.y hoặc x.y.z (ví dụ: 1.0, 1.2.3)';
    }

    if (formData.download_url && !isValidUrl(formData.download_url)) {
      newErrors.download_url = 'URL tải xuống không hợp lệ';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const installerMeta = buildInstallerPayload(uploadedInstaller, formData.download_url, editingVersion);

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isUploading) {
      return;
    }

    if (!validateForm()) {
      setInvalidSubmitCount(count => count + 1);
      return;
    }

    try {
        await onSubmit({
          platform: formData.platform as Platform,
          channel: formData.channel as Channel,
          version: formData.version,
          mandatory: formData.mandatory,
          active: formData.active,
          release_notes: formData.release_notes,
          download_url: formData.download_url,
          ...installerMeta
        });
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // File vừa upload nằm trong thư mục và mang tên theo nền tảng + kênh +
      // phiên bản cũ, đổi một trong ba thì link đó không còn đúng nữa
      if (
        (field === 'platform' || field === 'channel' || field === 'version') &&
        uploadedInstaller &&
        prev.download_url === uploadedInstaller.url
      ) {
        next.download_url = '';
      }
      // Kênh của nền tảng cũ không tồn tại ở nền tảng mới
      if (field === 'platform') {
        next.channel = DEFAULT_CHANNELS[value as Platform] ?? '';
      }
      // Số hiệu đến từ mô tả của cặp file cũ; đổi nền tảng hay kênh là phải
      // upload lại, giữ số cũ là để nó lệch với file sắp nộp
      if (field === 'platform' || field === 'channel') {
        next.version = '';
      }
      return next;
    });
    if (field === 'platform' || field === 'channel') {
      setUploadedInstaller(null);
    }
    if (field === 'download_url' && uploadedInstaller && value !== uploadedInstaller.url) {
      setUploadedInstaller(null);
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  const selectedPlatform = formData.platform as Platform;
  const selectedChannel = formData.channel as Channel;
  const channels = CHANNELS[selectedPlatform] ?? [];
  const supportsInstaller = installerExtensions(selectedPlatform, selectedChannel).length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900">
            {editingVersion ? 'Chỉnh sửa phiên bản' : 'Tạo phiên bản mới'}
          </h3>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
          {/* Platform */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nền tảng *
            </label>
            <select
              value={formData.platform}
              onChange={(e) => handleInputChange('platform', e.target.value)}
              aria-invalid={errors.platform ? true : undefined}
              data-invalid={errors.platform ? '' : undefined}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.platform ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={!!editingVersion} // Don't allow changing platform when editing
            >
              <option value="">Chọn nền tảng</option>
              {PLATFORMS.map(platform => (
                <option key={platform.value} value={platform.value}>
                  {platform.label}
                </option>
              ))}
            </select>
            {errors.platform && (
              <p className="mt-1 text-sm text-red-600">{errors.platform}</p>
            )}
          </div>

          {/* Channel — chỉ hiện khi nền tảng có nhiều hơn một kênh */}
          {channels.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kênh phát hành *
              </label>
              <select
                value={formData.channel}
                onChange={(e) => handleInputChange('channel', e.target.value)}
                aria-invalid={errors.channel ? true : undefined}
                data-invalid={errors.channel ? '' : undefined}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.channel ? 'border-red-500' : 'border-gray-300'
                }`}
                // Kênh nằm trong bộ khoá duy nhất cùng nền tảng và phiên bản
                disabled={!!editingVersion}
              >
                {channels.map(value => (
                  <option key={value} value={value}>
                    {CHANNEL_LABELS[value]}
                  </option>
                ))}
              </select>
              {errors.channel && (
                <p className="mt-1 text-sm text-red-600">{errors.channel}</p>
              )}
            </div>
          )}

          {/* Version */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phiên bản *
            </label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => handleInputChange('version', e.target.value)}
              aria-invalid={errors.version ? true : undefined}
              // Ô chỉ đọc thì kéo tới không giúp được gì, chỗ cần thao tác là khung upload bên dưới
              data-invalid={errors.version && !supportsInstaller ? '' : undefined}
              placeholder={
                supportsInstaller ? 'Điền từ file mô tả sau khi upload' : 'Ví dụ: 1.0, 1.2.3'
              }
              // Kênh có file cài thì số hiệu đọc từ file mô tả. Để gõ tay là mở
              // lại đúng chỗ người ta gõ lệch với version thật trong artifact.
              readOnly={supportsInstaller}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.version ? 'border-red-500' : 'border-gray-300'
              } ${supportsInstaller ? 'bg-gray-50 text-gray-600' : ''}`}
              disabled={!!editingVersion} // Don't allow changing version when editing
            />
            {supportsInstaller && !errors.version && (
              <p className="mt-1 text-xs text-gray-500">
                Đọc từ file mô tả đi kèm bản cài, không nhập tay.
              </p>
            )}
            {errors.version && (
              <p className="mt-1 text-sm text-red-600">{errors.version}</p>
            )}
          </div>

          {/* Mandatory */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.mandatory}
                onChange={(e) => handleInputChange('mandatory', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Bắt buộc cập nhật</span>
            </label>
          </div>

          {/* Active */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleInputChange('active', e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Kích hoạt phiên bản</span>
            </label>
          </div>

          {/* Release Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ghi chú phát hành
            </label>
            <textarea
              value={formData.release_notes}
              onChange={(e) => handleInputChange('release_notes', e.target.value)}
              placeholder="Mô tả các thay đổi trong phiên bản này..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Download URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {supportsInstaller ? 'File cài đặt' : 'URL tải xuống'}
            </label>
            {supportsInstaller && (
              <div className="mb-2" data-invalid={errors.version ? '' : undefined}>
                <InstallerUploadField
                  platform={selectedPlatform}
                  channel={selectedChannel}
                  uploaded={uploadedInstaller}
                  onUploaded={(result) => {
                    setUploadedInstaller(result);
                    // Số hiệu về từ file mô tả, không phải từ ô người gõ
                    setFormData(prev => ({
                      ...prev,
                      download_url: result.url,
                      version: result.version
                    }));
                    setErrors(prev => ({ ...prev, download_url: '', version: '' }));
                  }}
                  onUploadingChange={setIsUploading}
                />
                <p className="mt-2 text-xs text-gray-500">Hoặc dán link file đã có sẵn:</p>
              </div>
            )}
            <input
              type="url"
              value={formData.download_url}
              onChange={(e) => handleInputChange('download_url', e.target.value)}
              aria-invalid={errors.download_url ? true : undefined}
              data-invalid={errors.download_url ? '' : undefined}
              disabled={isUploading}
              placeholder={
                formData.platform === 'android'
                  ? 'https://play.google.com/store/apps/details?id=...'
                  : formData.platform === 'ios' || formData.platform === 'ipad'
                    ? 'https://apps.apple.com/vn/app/...'
                    : 'https://example.com/download/...'
              }
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.download_url ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.download_url && (
              <p className="mt-1 text-sm text-red-600">{errors.download_url}</p>
            )}
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={isLoading}
          >
            Hủy
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isUploading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Đang xử lý...
              </div>
            ) : (
              editingVersion ? 'Cập nhật' : 'Tạo mới'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateReaderVersionModal;
